import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Plus, Trash2, Target, Flag, CheckSquare, Building2, Users as UsersIcon, Loader2,
  ChevronDown, Users, LogOut
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import ModeToggle from '@/components/ModeToggle';
import MembersDialog from '@/components/MembersDialog';
import FamilyVisionBoard from '@/components/FamilyVisionBoard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVEL_META = {
  vision: { icon: Flag, label: 'corporate.level.vision', child: 'okr' },
  okr: { icon: Target, label: 'corporate.level.okr', child: 'kr' },
  kr: { icon: CheckSquare, label: 'corporate.level.kr', child: null }
};

/**
 * One workspace-management page that serves both Corporate and Family modes.
 *
 * The `mode` prop ('corporate' | 'family') determines:
 *   - Which workspaces are visible (filter by workspace.type === mode)
 *   - Which active-workspace selection persists (per-mode localStorage key)
 *   - Whether the body renders OKR cascade (corporate) or vision board (family)
 *   - The type assigned to newly created workspaces (no picker — implied by mode)
 *   - Header icon, titles, copy
 *
 * The route mounts two instances: <WorkspacePage mode="corporate" /> at /corporate
 * and <WorkspacePage mode="family" /> at /family.
 */
export default function WorkspacePage({ token, user, setToken, mode }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isCorporate = mode === 'corporate';
  const isFamily = mode === 'family';
  const activeWsKey = `align_active_workspace_${mode}`;
  const HeaderIcon = isFamily ? UsersIcon : Building2;

  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(() => {
    try { return localStorage.getItem(activeWsKey) || null; } catch (_) { return null; }
  });
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membersOpen, setMembersOpen] = useState(false);

  const [showWorkspaceForm, setShowWorkspaceForm] = useState(false);
  const [workspaceForm, setWorkspaceForm] = useState({ name: '', description: '' });
  const [savingWorkspace, setSavingWorkspace] = useState(false);

  // addingGoal: { level: 'vision'|'okr'|'kr', parent_id: string|null } | null
  const [addingGoal, setAddingGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({ title: '', description: '' });
  const [savingGoal, setSavingGoal] = useState(false);

  const axiosConfig = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // Workspaces filtered to the current mode. Backend returns all workspaces the
  // user is a member of; we filter client-side because the set is small.
  const workspaces = useMemo(
    () => allWorkspaces.filter((w) => w.type === mode),
    [allWorkspaces, mode]
  );

  const fetchWorkspaces = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/workspaces`, axiosConfig);
      setAllWorkspaces(data);
      const mineForMode = data.filter((w) => w.type === mode);
      if (mineForMode.length > 0) {
        const stillThere = mineForMode.find((w) => w.id === activeWorkspaceId);
        if (!stillThere) setActiveWorkspaceId(mineForMode[0].id);
      } else {
        setActiveWorkspaceId(null);
        setGoals([]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error(t('toast.session.expired'));
        setToken(null);
        navigate('/');
      } else {
        toast.error(t('corporate.workspaces.fetchError'));
      }
    } finally {
      setLoading(false);
    }
  }, [axiosConfig, activeWorkspaceId, mode, t, setToken, navigate]);

  const fetchGoals = useCallback(async (workspaceId) => {
    if (!workspaceId || isFamily) return;  // family mode uses vision board, not goals
    try {
      const { data } = await axios.get(`${API}/workspaces/${workspaceId}/goals/cascading`, axiosConfig);
      setGoals(data);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(t('corporate.goals.fetchError'));
      }
    }
  }, [axiosConfig, isFamily, t]);

  useEffect(() => { fetchWorkspaces(); }, [fetchWorkspaces]);

  useEffect(() => {
    if (activeWorkspaceId) {
      try { localStorage.setItem(activeWsKey, activeWorkspaceId); } catch (_) {}
      fetchGoals(activeWorkspaceId);
    }
  }, [activeWorkspaceId, activeWsKey, fetchGoals]);

  const handleLogout = () => { setToken(null); navigate('/'); };

  // ============ Workspace actions ============

  const createWorkspace = async (e) => {
    e.preventDefault();
    if (!workspaceForm.name.trim()) return;
    setSavingWorkspace(true);
    try {
      const payload = { ...workspaceForm, type: mode };  // type implied by current mode
      const { data } = await axios.post(`${API}/workspaces`, payload, axiosConfig);
      toast.success(t('corporate.workspace.created'));
      setWorkspaceForm({ name: '', description: '' });
      setShowWorkspaceForm(false);
      setAllWorkspaces((ws) => [...ws, data]);
      setActiveWorkspaceId(data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('corporate.workspace.createError'));
    } finally {
      setSavingWorkspace(false);
    }
  };

  // ============ Goal actions (corporate mode only) ============

  const openAddGoal = (level, parent_id) => {
    setAddingGoal({ level, parent_id });
    setGoalForm({ title: '', description: '' });
  };

  const createGoal = async (e) => {
    e.preventDefault();
    if (!addingGoal || !goalForm.title.trim()) return;
    setSavingGoal(true);
    try {
      await axios.post(
        `${API}/goals/cascading`,
        {
          workspace_id: activeWorkspaceId,
          title: goalForm.title,
          description: goalForm.description,
          level: addingGoal.level,
          parent_id: addingGoal.parent_id
        },
        axiosConfig
      );
      toast.success(t('corporate.goal.created'));
      setAddingGoal(null);
      setGoalForm({ title: '', description: '' });
      fetchGoals(activeWorkspaceId);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('corporate.goal.createError'));
    } finally {
      setSavingGoal(false);
    }
  };

  const updateProgress = async (goalId, progress) => {
    try {
      await axios.put(`${API}/goals/cascading/${goalId}/progress?progress=${progress}`, null, axiosConfig);
      fetchGoals(activeWorkspaceId);
    } catch (error) {
      toast.error(t('corporate.goal.updateError'));
    }
  };

  const deleteGoal = async (goalId) => {
    if (!window.confirm(t('corporate.goal.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/goals/cascading/${goalId}`, axiosConfig);
      toast.success(t('corporate.goal.deleted'));
      fetchGoals(activeWorkspaceId);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('corporate.goal.deleteError'));
    }
  };

  // ============ Goal tree shape ============

  const goalTree = useMemo(() => {
    const visions = goals.filter((g) => g.level === 'vision');
    return visions.map((v) => ({
      ...v,
      okrs: goals
        .filter((g) => g.level === 'okr' && g.parent_id === v.id)
        .map((okr) => ({
          ...okr,
          krs: goals.filter((g) => g.level === 'kr' && g.parent_id === okr.id)
        }))
    }));
  }, [goals]);

  const renderHeaderTrialBadge = () => {
    if (user?.subscription?.status !== 'trialing' || !user?.subscription?.trial_end) return null;
    
    const trialEnd = new Date(user.subscription.trial_end);
    const now = new Date();
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return null;
    
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border border-emerald-500/20 whitespace-nowrap flex-shrink-0" data-testid="header-trial-badge">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
        Pro Trial ({diffDays}d left)
      </span>
    );
  };

  // ============ Render ============

  return (
    <div className="min-h-screen bg-background" data-testid={`workspace-page-${mode}`}>
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-6 min-w-0">
            <h1 className="font-serif text-xl sm:text-3xl font-light tracking-tight flex-shrink-0">{t('landing.title')}</h1>
            <div className="flex items-center gap-2 sm:gap-3">
              <ModeToggle active={mode} user={user} />
              {renderHeaderTrialBadge()}
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            {workspaces.length > 0 && activeWorkspace && (
              <WorkspaceSelector
                workspaces={workspaces}
                activeId={activeWorkspaceId}
                onChange={setActiveWorkspaceId}
                onCreate={() => setShowWorkspaceForm(true)}
                HeaderIcon={HeaderIcon}
                t={t}
                mode={mode}
              />
            )}
            {activeWorkspace && (
              <Button
                onClick={() => setMembersOpen(true)}
                variant="ghost"
                size="icon"
                className="rounded-full sm:size-auto sm:px-3 text-xs sm:text-sm h-8 w-8 sm:w-auto"
                title={t('members.title')}
                aria-label={t('members.title')}
                data-testid="open-members-btn"
              >
                <Users className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">{t('members.title')}</span>
              </Button>
            )}
            <LanguageSwitcher />
            <Button onClick={handleLogout} variant="ghost" size="icon" className="rounded-full sm:size-auto sm:px-3 text-xs sm:text-sm h-8 w-8 sm:w-auto" title={t('nav.logout')} aria-label={t('nav.logout')} data-testid="logout-btn">
              <LogOut className="w-4 h-4 sm:hidden" />
              <span className="hidden sm:inline">{t('nav.logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span>{t('corporate.loading')}</span>
          </div>
        ) : showWorkspaceForm || workspaces.length === 0 ? (
          <WorkspaceCreateCard
            mode={mode}
            HeaderIcon={HeaderIcon}
            form={workspaceForm}
            setForm={setWorkspaceForm}
            onSubmit={createWorkspace}
            onCancel={workspaces.length > 0 ? () => setShowWorkspaceForm(false) : null}
            saving={savingWorkspace}
            firstTime={workspaces.length === 0}
            t={t}
          />
        ) : isFamily ? (
          <FamilyVisionBoard workspace={activeWorkspace} token={token} />
        ) : (
          <CascadeView
            workspace={activeWorkspace}
            tree={goalTree}
            onAddGoal={openAddGoal}
            onUpdateProgress={updateProgress}
            onDeleteGoal={deleteGoal}
            addingGoal={addingGoal}
            goalForm={goalForm}
            setGoalForm={setGoalForm}
            onSubmitGoal={createGoal}
            onCancelGoal={() => setAddingGoal(null)}
            savingGoal={savingGoal}
            t={t}
          />
        )}
      </div>

      <MembersDialog
        open={membersOpen}
        onOpenChange={setMembersOpen}
        workspace={activeWorkspace}
        token={token}
        currentUserId={user?.id}
      />
    </div>
  );
}

// ============ Sub-components ============

function WorkspaceSelector({ workspaces, activeId, onChange, onCreate, HeaderIcon, t, mode }) {
  return (
    <div className="relative">
      <select
        value={activeId || ''}
        onChange={(e) => {
          if (e.target.value === '__new__') {
            onCreate();
            return;
          }
          onChange(e.target.value);
        }}
        className="appearance-none bg-secondary/40 border border-border/60 rounded-full pl-7 sm:pl-8 pr-7 sm:pr-8 py-1.5 text-xs sm:text-sm font-medium cursor-pointer hover:bg-secondary/60 transition-colors max-w-[110px] sm:max-w-[200px] truncate"
        data-testid={`workspace-selector-${mode}`}
      >
        {workspaces.map((w) => (
          <option key={w.id} value={w.id}>{w.name}</option>
        ))}
        <option value="__new__">+ {t(`workspace.${mode}.newOption`)}</option>
      </select>
      <HeaderIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
    </div>
  );
}

function WorkspaceCreateCard({ mode, HeaderIcon, form, setForm, onSubmit, onCancel, saving, firstTime, t }) {
  const titleKey = firstTime ? `workspace.${mode}.firstTime.title` : `workspace.${mode}.newTitle`;
  const subtitleKey = firstTime ? `workspace.${mode}.firstTime.subtitle` : `workspace.${mode}.newSubtitle`;
  const ctaKey = firstTime ? `workspace.${mode}.firstTime.cta` : `workspace.${mode}.createCta`;
  return (
    <div className="max-w-xl mx-auto">
      <div className="text-center mb-8 fade-in">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-accent/10 mb-4">
          <HeaderIcon className="w-7 h-7 text-accent" />
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl font-light tracking-tight mb-2">{t(titleKey)}</h2>
        <p className="text-sm text-muted-foreground font-serif italic max-w-md mx-auto leading-relaxed">{t(subtitleKey)}</p>
      </div>

      <form
        onSubmit={onSubmit}
        className="bg-card/50 border border-border rounded-2xl p-6 sm:p-8 space-y-4 fade-in"
        data-testid={`workspace-create-form-${mode}`}
      >
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">{t('corporate.workspace.nameLabel')}</label>
          <Input
            placeholder={t(`workspace.${mode}.namePlaceholder`)}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            autoFocus
            data-testid="workspace-name-input"
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">{t('corporate.workspace.descLabel')}</label>
          <Textarea
            placeholder={t(`workspace.${mode}.descPlaceholder`)}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            className="resize-none"
            data-testid="workspace-desc-input"
          />
        </div>
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={saving || !form.name.trim()} className="rounded-full" data-testid="workspace-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {t(ctaKey)}
          </Button>
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel} disabled={saving} className="rounded-full">
              {t('form.cancel')}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

function CascadeView({
  workspace, tree, onAddGoal, onUpdateProgress, onDeleteGoal,
  addingGoal, goalForm, setGoalForm, onSubmitGoal, onCancelGoal, savingGoal, t
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl sm:text-3xl font-light tracking-tight">{workspace?.name}</h2>
          {workspace?.description && (
            <p className="text-sm text-muted-foreground mt-1 font-serif italic">{workspace.description}</p>
          )}
        </div>
        <Button
          onClick={() => onAddGoal('vision', null)}
          size="sm"
          className="rounded-full text-xs sm:text-sm"
          data-testid="add-vision-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> {t('corporate.vision.add')}
        </Button>
      </div>

      {addingGoal && addingGoal.level === 'vision' && (
        <GoalForm
          level="vision"
          form={goalForm}
          setForm={setGoalForm}
          onSubmit={onSubmitGoal}
          onCancel={onCancelGoal}
          saving={savingGoal}
          t={t}
        />
      )}

      {tree.length === 0 && !addingGoal ? (
        <div className="text-center py-16 text-muted-foreground">
          <Flag className="w-8 h-8 mx-auto mb-3 opacity-40" />
          <p className="font-serif italic max-w-sm mx-auto leading-relaxed">{t('corporate.vision.empty')}</p>
        </div>
      ) : (
        tree.map((vision) => (
          <VisionCard
            key={vision.id}
            vision={vision}
            onAddGoal={onAddGoal}
            onUpdateProgress={onUpdateProgress}
            onDeleteGoal={onDeleteGoal}
            addingGoal={addingGoal}
            goalForm={goalForm}
            setGoalForm={setGoalForm}
            onSubmitGoal={onSubmitGoal}
            onCancelGoal={onCancelGoal}
            savingGoal={savingGoal}
            t={t}
          />
        ))
      )}
    </div>
  );
}

function VisionCard({ vision, onAddGoal, onUpdateProgress, onDeleteGoal, addingGoal, goalForm, setGoalForm, onSubmitGoal, onCancelGoal, savingGoal, t }) {
  return (
    <article className="rounded-2xl border border-border bg-card/40 p-5 sm:p-6 space-y-4 fade-in" data-testid={`vision-${vision.id}`}>
      <GoalHeader goal={vision} onDelete={onDeleteGoal} onProgress={onUpdateProgress} large t={t} />

      <div className="pl-3 sm:pl-6 border-l-2 border-accent/20 space-y-4">
        {vision.okrs.map((okr) => (
          <div key={okr.id} className="space-y-3" data-testid={`okr-${okr.id}`}>
            <GoalHeader goal={okr} onDelete={onDeleteGoal} onProgress={onUpdateProgress} t={t} />
            <div className="pl-3 sm:pl-6 border-l-2 border-accent/10 space-y-2">
              {okr.krs.map((kr) => (
                <GoalRow key={kr.id} goal={kr} onDelete={onDeleteGoal} onProgress={onUpdateProgress} t={t} />
              ))}
              {addingGoal && addingGoal.level === 'kr' && addingGoal.parent_id === okr.id ? (
                <GoalForm level="kr" form={goalForm} setForm={setGoalForm} onSubmit={onSubmitGoal} onCancel={onCancelGoal} saving={savingGoal} t={t} inline />
              ) : (
                <button
                  type="button"
                  onClick={() => onAddGoal('kr', okr.id)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 py-1"
                  data-testid={`add-kr-btn-${okr.id}`}
                >
                  <Plus className="w-3 h-3" /> {t('corporate.kr.add')}
                </button>
              )}
            </div>
          </div>
        ))}
        {addingGoal && addingGoal.level === 'okr' && addingGoal.parent_id === vision.id ? (
          <GoalForm level="okr" form={goalForm} setForm={setGoalForm} onSubmit={onSubmitGoal} onCancel={onCancelGoal} saving={savingGoal} t={t} inline />
        ) : (
          <button
            type="button"
            onClick={() => onAddGoal('okr', vision.id)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 py-1"
            data-testid={`add-okr-btn-${vision.id}`}
          >
            <Plus className="w-3 h-3" /> {t('corporate.okr.add')}
          </button>
        )}
      </div>
    </article>
  );
}

function GoalHeader({ goal, onDelete, onProgress, large, t }) {
  const Icon = LEVEL_META[goal.level].icon;
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icon className={`${large ? 'w-4 h-4' : 'w-3.5 h-3.5'} text-accent flex-shrink-0`} />
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t(LEVEL_META[goal.level].label)}</span>
            {goal.owner_name && (
              <span className="text-[10px] text-muted-foreground">· {goal.owner_name}</span>
            )}
          </div>
          <h3 className={`${large ? 'font-serif text-lg sm:text-xl font-light leading-snug' : 'font-medium text-sm sm:text-base'}`}>{goal.title}</h3>
          {goal.description && (
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">{goal.description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onDelete(goal.id)}
          className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
          data-testid={`delete-goal-${goal.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <ProgressControl progress={goal.progress || 0} onChange={(v) => onProgress(goal.id, v)} t={t} />
    </div>
  );
}

function GoalRow({ goal, onDelete, onProgress, t }) {
  return (
    <div className="flex items-center gap-3 group" data-testid={`kr-${goal.id}`}>
      <CheckSquare className="w-3.5 h-3.5 text-accent flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm">{goal.title}</p>
        {goal.description && (
          <p className="text-xs text-muted-foreground">{goal.description}</p>
        )}
      </div>
      <div className="w-32 flex items-center gap-2">
        <input
          type="range"
          min="0"
          max="100"
          step="5"
          value={goal.progress || 0}
          onChange={(e) => onProgress(goal.id, parseFloat(e.target.value))}
          className="flex-1 accent-accent"
          aria-label={t('corporate.goal.progress')}
        />
        <span className="text-xs font-medium text-muted-foreground w-9 text-right">{Math.round(goal.progress || 0)}%</span>
      </div>
      <button
        type="button"
        onClick={() => onDelete(goal.id)}
        className="text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
        data-testid={`delete-goal-${goal.id}`}
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

function ProgressControl({ progress, onChange, t }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
        <div
          className="h-full bg-accent transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="text-xs font-medium text-muted-foreground tabular-nums w-9 text-right">{Math.round(progress)}%</span>
      <input
        type="range"
        min="0"
        max="100"
        step="5"
        value={progress}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-24 accent-accent"
        aria-label={t('corporate.goal.progress')}
      />
    </div>
  );
}

function GoalForm({ level, form, setForm, onSubmit, onCancel, saving, t, inline }) {
  return (
    <form
      onSubmit={onSubmit}
      className={inline ? 'p-3 rounded-lg border border-accent/30 bg-accent/5 space-y-2' : 'p-4 sm:p-5 rounded-xl border border-accent/30 bg-accent/5 space-y-3 fade-in'}
      data-testid={`goal-form-${level}`}
    >
      <Input
        placeholder={t(`corporate.${level}.titlePlaceholder`)}
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        required
        autoFocus
        className="text-sm"
        data-testid={`goal-${level}-title-input`}
      />
      <Textarea
        placeholder={t(`corporate.${level}.descPlaceholder`)}
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        rows={2}
        className="resize-none text-xs sm:text-sm"
      />
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={saving || !form.title.trim()} className="rounded-full text-xs" data-testid={`goal-${level}-submit-btn`}>
          {saving ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : null}
          {t('form.create')}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={saving} className="text-xs">
          {t('form.cancel')}
        </Button>
      </div>
    </form>
  );
}
